import { COLORS } from "../shared/constants";
import { IconX } from "../shared/icons";
import { localDateStr } from "../shared/utils";
import { useState } from "react";

export default // ─── Assessment Entry Form ────────────────────────────────────
function AssessmentEntryForm({ scales, treatments, initialTreatmentId, initialScaleId, patient, onSubmit, onGenerateLink, onCancel }) {
  const [scaleId, setScaleId] = useState(initialScaleId || "");
  const [treatmentId, setTreatmentId] = useState(initialTreatmentId || treatments[0]?.id || "");
  const [mode, setMode] = useState("item"); // "item" | "total"
  const [responses, setResponses] = useState({});
  const [totalOnly, setTotalOnly] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [assessmentDate, setAssessmentDate] = useState(localDateStr(new Date()));

  const activeScales = scales.filter(s => s.is_active !== false);
  const scale = scales.find(s => s.id === scaleId);
  const questions = scale?.questions || [];

  const scoredQuestions = questions.filter(q => q.response_type !== "FREE_TEXT");
  const runningTotal = scoredQuestions.reduce((sum, q) => sum + (parseFloat(responses[q.id]) || 0), 0);
  const maxScore = scale?.max_score || 0;
  const severity = scale?.severity_bands?.find(b => runningTotal >= b.min_score && runningTotal <= b.max_score);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const data = {
        patient_id: patient.id,
        treatment_assignment_id: treatmentId || null,
        scale_id: scaleId,
        notes: notes || null,
        administered_at: assessmentDate ? `${assessmentDate}T12:00:00` : null,
      };
      if (mode === "total") {
        const score = parseFloat(totalOnly);
        if (score < 0 || score > maxScore) return;
        data.total_score = score;
        data.administration_mode = "MANUAL_ENTRY";
      } else {
        data.responses = questions.map((q, i) => {
          if (q.response_type === "FREE_TEXT") {
            return {
              question_id: q.id,
              response_value: null,
              response_text: responses[q.id] || "",
              sort_order: i + 1,
            };
          }
          return {
            question_id: q.id,
            response_value: parseFloat(responses[q.id]) || 0,
            sort_order: i + 1,
          };
        });
        data.administration_mode = scale?.administration_mode === "CLINICIAN" ? "CLINICIAN" : "MANUAL_ENTRY";
      }
      await onSubmit(data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: "20px", margin: "0 0 16px", background: COLORS.inputBg, borderRadius: "10px", border: `1px solid ${COLORS.borderLight}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <span style={{ fontSize: "18px", fontWeight: "600", color: COLORS.text }}>Administer Assessment</span>
        <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textSecondary }}><IconX /></button>
      </div>

      {/* Scale + Treatment + Date Selection */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "16px" }}>
        <div>
          <label style={{ fontSize: "13px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>SCALE</label>
          <select value={scaleId} onChange={(e) => { setScaleId(e.target.value); setResponses({}); setTotalOnly(""); }}
            style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "15px", background: "#FFF" }}>
            <option value="">Select scale...</option>
            {activeScales.map(s => <option key={s.id} value={s.id}>{s.short_name} — {s.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: "13px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>TREATMENT</label>
          <select value={treatmentId} onChange={(e) => setTreatmentId(e.target.value)}
            style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "15px", background: "#FFF" }}>
            {treatments.map(t => <option key={t.id} value={t.id}>{t.protocol_name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: "13px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>DATE</label>
          <input type="date" value={assessmentDate} onChange={(e) => setAssessmentDate(e.target.value)}
            style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "15px", background: "#FFF", boxSizing: "border-box" }} />
        </div>
      </div>

      {scaleId && (
        <>
          {/* Mode toggle + Send to Patient */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <div style={{ display: "flex", gap: "4px", background: "#FFF", borderRadius: "8px", padding: "3px", border: `1px solid ${COLORS.borderLight}` }}>
              <button onClick={() => setMode("item")}
                style={{ padding: "6px 14px", borderRadius: "6px", border: "none", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                  background: mode === "item" ? COLORS.primaryLight : "transparent", color: mode === "item" ? COLORS.primary : COLORS.textSecondary }}>
                Item-by-Item
              </button>
              <button onClick={() => setMode("total")}
                style={{ padding: "6px 14px", borderRadius: "6px", border: "none", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                  background: mode === "total" ? COLORS.primaryLight : "transparent", color: mode === "total" ? COLORS.primary : COLORS.textSecondary }}>
                Total Only
              </button>
            </div>
            {scale?.administration_mode !== "CLINICIAN" && (
              <button onClick={() => onGenerateLink({ patient_id: patient.id, treatment_assignment_id: treatmentId, scale_id: scaleId })}
                style={{ height: "30px", padding: "0 12px", borderRadius: "6px", border: `1px solid ${COLORS.accent}`, background: "#E8F8F0", color: COLORS.accent, fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
                📱 Send to Patient
              </button>
            )}
            {scale?.administration_mode === "CLINICIAN" && (
              <span style={{ fontSize: "11px", color: COLORS.warning, fontWeight: "500" }}>Clinician-administered scale</span>
            )}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "28px", fontWeight: "700", color: COLORS.text }}>{mode === "total" ? (totalOnly || "—") : runningTotal}</span>
              <span style={{ fontSize: "14px", color: COLORS.textSecondary }}>/ {maxScore}</span>
              {severity && mode === "item" && (
                <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "700", background: severity.color + "22", color: severity.color }}>{severity.label}</span>
              )}
            </div>
          </div>

          {/* Item-by-item responses */}
          {mode === "item" && (
            <div style={{ marginBottom: "12px" }}>
              {questions.map((q, idx) => {
                // Conditional visibility: if question has conditional_on, check parent answer
                const condOpts = q.response_options?.conditional_on ? q.response_options : null;
                if (condOpts) {
                  const parentVal = responses[condOpts.conditional_on];
                  // Hide Q3 if Q2 answer is "No" (0) or not yet answered
                  if (parentVal == null || parentVal === 0) return null;
                }

                // Determine overrides from conditional config
                let vasMinLabel = q.vas_min_label || "";
                let vasMaxLabel = q.vas_max_label || "";
                let vasMin = q.vas_min != null ? q.vas_min : 0;
                let vasMax = q.vas_max != null ? q.vas_max : 100;
                let questionText = q.question_text;
                let defaultValue = null;
                if (condOpts) {
                  const parentVal = responses[condOpts.conditional_on];
                  const branch = parentVal === 1 ? condOpts.better : parentVal === -1 ? condOpts.worse : null;
                  if (branch) {
                    vasMinLabel = branch.vas_min_label || vasMinLabel;
                    vasMaxLabel = branch.vas_max_label || vasMaxLabel;
                    if (branch.vas_min != null) vasMin = branch.vas_min;
                    if (branch.vas_max != null) vasMax = branch.vas_max;
                    if (branch.question_text) questionText = branch.question_text;
                    if (branch.default_value != null) defaultValue = branch.default_value;
                  }
                }

                const vasStep = (vasMax - vasMin) <= 20 ? 0.1 : 1;
                const vasMidpoint = Math.round((vasMax + vasMin) / 2 * 10) / 10;
                const vasCurrentValue = responses[q.id] != null
                  ? responses[q.id]
                  : (defaultValue != null ? defaultValue : vasMidpoint);

                return (
                  <div key={q.id} style={{ padding: "14px 0", borderBottom: idx < questions.length - 1 ? `1px solid ${COLORS.borderLight}` : "none" }}>
                    <div style={{ fontSize: "15px", color: COLORS.text, marginBottom: "10px", lineHeight: "1.5" }}>
                      <span style={{ fontWeight: "600", color: COLORS.primary }}>{q.question_number}.</span> {questionText}
                    </div>

                    {/* VAS Slider */}
                    {q.response_type === "VAS" && (
                      <div style={{ padding: "4px 0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <span style={{ fontSize: "11px", color: COLORS.textSecondary, minWidth: "80px", textAlign: "right" }}>
                            {vasMinLabel || vasMin}
                          </span>
                          <div style={{ flex: 1 }}>
                            <input type="range"
                              min={vasMin} max={vasMax}
                              step={vasStep}
                              value={vasCurrentValue}
                              onChange={(e) => setResponses(prev => ({ ...prev, [q.id]: parseFloat(e.target.value) }))}
                              style={{ width: "100%", accentColor: COLORS.primary, height: "8px" }} />
                          </div>
                          <span style={{ fontSize: "11px", color: COLORS.textSecondary, minWidth: "80px" }}>
                            {vasMaxLabel || vasMax}
                          </span>
                        </div>
                        <div style={{ textAlign: "center", marginTop: "4px" }}>
                          <span style={{ fontSize: "24px", fontWeight: "700", color: COLORS.primary }}>
                            {vasCurrentValue}
                          </span>
                          <span style={{ fontSize: "12px", color: COLORS.textSecondary, marginLeft: "4px" }}>
                            / {vasMax}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* CHOICE buttons (e.g. Worse / No / Better) */}
                    {q.response_type === "CHOICE" && (
                      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        {(q.response_options || []).map((opt) => {
                          const isSelected = responses[q.id] === opt.value;
                          const btnColor = opt.value === -1 ? "#E74C3C" : opt.value === 1 ? "#2ECC71" : COLORS.textSecondary;
                          return (
                            <button key={opt.value} onClick={() => setResponses(prev => ({ ...prev, [q.id]: opt.value }))}
                              style={{
                                padding: "10px 24px", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer",
                                border: `2px solid ${isSelected ? btnColor : COLORS.border}`,
                                background: isSelected ? btnColor + "18" : "#FFF",
                                color: isSelected ? btnColor : COLORS.text,
                              }}>
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* LIKERT radio buttons (default) */}
                    {(q.response_type === "LIKERT" || (!q.response_type && q.response_options)) && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {(q.response_options || []).map((opt) => (
                          <button key={opt.value} onClick={() => setResponses(prev => ({ ...prev, [q.id]: opt.value }))}
                            style={{
                              padding: "7px 14px", borderRadius: "6px", fontSize: "14px", cursor: "pointer",
                              border: `1px solid ${responses[q.id] === opt.value ? COLORS.primary : COLORS.border}`,
                              background: responses[q.id] === opt.value ? COLORS.primaryLight : "#FFF",
                              color: responses[q.id] === opt.value ? COLORS.primary : COLORS.text,
                              fontWeight: responses[q.id] === opt.value ? "600" : "400",
                            }}>
                            {opt.value}{opt.label ? ` — ${opt.label}` : ""}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* FREE_TEXT textarea */}
                    {q.response_type === "FREE_TEXT" && (
                      <textarea
                        value={responses[q.id] || ""}
                        onChange={e => setResponses(prev => ({ ...prev, [q.id]: e.target.value }))}
                        placeholder="Enter response..."
                        rows={3}
                        style={{
                          width: "100%", padding: "8px 10px", borderRadius: "8px",
                          border: `1px solid ${COLORS.border}`, fontSize: "13px",
                          color: COLORS.text, background: "#FFF", resize: "vertical",
                          boxSizing: "border-box", fontFamily: "inherit", lineHeight: "1.4",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Total-only entry */}
          {mode === "total" && (
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>TOTAL SCORE</label>
              <input type="number" min="0" max={maxScore} value={totalOnly} onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || (parseFloat(val) >= 0 && parseFloat(val) <= maxScore)) setTotalOnly(val);
                }}
                style={{ width: "120px", height: "40px", padding: "0 12px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "18px", fontWeight: "600", textAlign: "center" }} />
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: "12px" }}>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
              style={{ width: "100%", height: "38px", padding: "0 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "14px" }} />
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={handleSubmit} disabled={submitting || (!scaleId) || (mode === "total" && !totalOnly) || (mode === "item" && scoredQuestions.length > 0 && scoredQuestions.every(q => responses[q.id] === undefined))}
              style={{ height: "40px", padding: "0 24px", borderRadius: "8px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "15px", fontWeight: "600",
                cursor: submitting ? "wait" : "pointer", opacity: submitting || (!scaleId) || (mode === "total" && !totalOnly) || (mode === "item" && scoredQuestions.length > 0 && scoredQuestions.every(q => responses[q.id] === undefined)) ? 0.5 : 1 }}>
              {submitting ? "Saving..." : "Save Score"}
            </button>
            <button onClick={onCancel}
              style={{ height: "40px", padding: "0 20px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "15px", fontWeight: "500", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}


// ─── Assessment Trend Graphs ──────────────────────────────────

